import { Button, Card } from '@evw/ui';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';
import {
  createConnectOnboardingLink,
  listPaymentOperations,
  refreshConnectStatus,
  requestRefund,
  resolvePaymentReview,
  type Payment
} from './payments-api';

function money(value: number | null, currency: string) {
  return value === null
    ? 'Pending'
    : (value / 100).toLocaleString(undefined, { style: 'currency', currency });
}

export function PaymentsPage({ organisationId }: { organisationId: string }) {
  const client = useQueryClient();
  const query = useQuery({
    queryKey: ['payment-operations', organisationId],
    queryFn: () => listPaymentOperations(organisationId)
  });
  const refresh = useMutation({
    mutationFn: () => refreshConnectStatus(organisationId),
    onSuccess: () => client.invalidateQueries({ queryKey: ['payment-operations', organisationId] })
  });
  const onboard = useMutation({
    mutationFn: () => createConnectOnboardingLink(organisationId),
    onSuccess: ({ url }) => window.location.assign(url)
  });
  const config = query.data?.configuration;
  const error = query.error ?? refresh.error ?? onboard.error;
  return (
    <div className="stack">
      <Card>
        <h2>Stripe Connect</h2>
        <p className="muted">
          The business is the Stripe merchant. Payments and refunds are created directly on its
          connected account.
        </p>
        <div className="status-grid">
          <span>
            <small>Onboarding</small>
            <strong>{config?.onboarding_status ?? 'Not started'}</strong>
          </span>
          <span>
            <small>Charges</small>
            <strong>{config?.charges_enabled ? 'Enabled' : 'Unavailable'}</strong>
          </span>
          <span>
            <small>Payouts</small>
            <strong>{config?.payouts_enabled ? 'Enabled' : 'Unavailable'}</strong>
          </span>
          <span>
            <small>Card capability</small>
            <strong>{config?.card_payments_capability ?? 'Unknown'}</strong>
          </span>
          <span>
            <small>Afterpay capability</small>
            <strong>{config?.afterpay_capability_status ?? 'Unknown'}</strong>
          </span>
          <span>
            <small>Mode</small>
            <strong>{config?.environment ?? 'Configured by server'}</strong>
          </span>
        </div>
        {config?.requirements_disabled_reason ? (
          <p className="error">Stripe restriction: {config.requirements_disabled_reason}</p>
        ) : null}
        {config?.requirements_currently_due?.length ? (
          <p className="warning">Information due: {config.requirements_currently_due.join(', ')}</p>
        ) : null}
        <div className="form-actions">
          <Button onClick={() => onboard.mutate()} disabled={onboard.isPending}>
            {config?.stripe_connected_account_id ? 'Continue onboarding' : 'Connect Stripe'}
          </Button>
          {config?.stripe_connected_account_id ? (
            <Button
              className="button-secondary"
              onClick={() => refresh.mutate()}
              disabled={refresh.isPending}
            >
              Refresh status
            </Button>
          ) : null}
        </div>
        {error ? <p className="error">{error.message}</p> : null}
      </Card>
      <Card>
        <h2>Payments</h2>
        <div className="records">
          {query.data?.payments.map((payment) => (
            <PaymentRecord
              key={payment.id}
              organisationId={organisationId}
              payment={payment}
              onChanged={() => {
                void client.invalidateQueries({
                  queryKey: ['payment-operations', organisationId]
                });
              }}
            />
          ))}
          {!query.data?.payments.length ? <p className="muted">No payments recorded.</p> : null}
        </div>
      </Card>
      <Card>
        <h2>Payment failures</h2>
        <div className="records">
          {query.data?.attempts
            .filter((item) => ['failed', 'cancelled', 'needs_review'].includes(item.status))
            .map((item) => (
              <article className="record" key={item.id}>
                <div>
                  <strong>
                    {money(item.requested_amount_minor, item.currency)} · {item.payment_option}
                  </strong>
                  <p>
                    {item.status} · {item.failure_code ?? 'No provider code'} ·{' '}
                    {item.failure_reason ?? 'No detail supplied'}
                  </p>
                </div>
              </article>
            ))}
          {!query.data?.attempts.some((item) =>
            ['failed', 'cancelled', 'needs_review'].includes(item.status)
          ) ? (
            <p className="muted">No payment failures.</p>
          ) : null}
        </div>
      </Card>
      <Card>
        <h2>Refund history</h2>
        <div className="records">
          {query.data?.refunds.map((item) => (
            <article className="record" key={item.id}>
              <div>
                <strong>{money(item.requested_amount_minor, item.currency)}</strong>
                <p>
                  {item.status} · refunded {money(item.refunded_amount_minor, item.currency)}
                  {item.failure_code ? ` · ${item.failure_code}` : ''}
                </p>
              </div>
            </article>
          ))}
          {!query.data?.refunds.length ? <p className="muted">No refunds recorded.</p> : null}
        </div>
      </Card>
      <Card>
        <h2>Needs review</h2>
        <div className="records">
          {query.data?.reviews.map((item) => (
            <article className="record" key={item.id}>
              <div>
                <strong>{item.reason_code}</strong>
                <p>
                  {item.status} · {item.reason_detail ?? 'Manual payment recovery required'}
                </p>
                <p>
                  {query.data.payments.find((payment) => payment.id === item.payment_id)
                    ?.customer_name ?? 'Affected customer'}
                </p>
              </div>
              <ReviewResolution caseId={item.id} organisationId={organisationId} />
            </article>
          ))}
          {!query.data?.reviews.length ? <p className="muted">No payment recovery cases.</p> : null}
        </div>
      </Card>
      <Card>
        <h2>Operational alerts</h2>
        <div className="records">
          {query.data?.alerts.map((item) => (
            <article className="record" key={item.id}>
              <div>
                <strong>{item.alert_type}</strong>
                <p>
                  {item.severity} · {item.status} · {new Date(item.created_at).toLocaleString()}
                </p>
              </div>
            </article>
          ))}
          {!query.data?.alerts.length ? <p className="muted">No open payment alerts.</p> : null}
        </div>
      </Card>
    </div>
  );
}

function ReviewResolution({ caseId, organisationId }: { caseId: string; organisationId: string }) {
  const client = useQueryClient();
  const [status, setStatus] = useState('investigating');
  const [notes, setNotes] = useState('');
  const save = useMutation({
    mutationFn: () => resolvePaymentReview(caseId, status, notes),
    onSuccess: () => client.invalidateQueries({ queryKey: ['payment-operations', organisationId] })
  });
  return (
    <form
      className="stack"
      onSubmit={(event) => {
        event.preventDefault();
        save.mutate();
      }}
    >
      <label>
        Case status
        <select value={status} onChange={(event) => setStatus(event.target.value)}>
          <option value="investigating">Investigating</option>
          <option value="resolved_refunded">Resolved with verified full refund</option>
          <option value="resolved_other">Resolved with documented agreement</option>
        </select>
      </label>
      <label>
        Resolution notes
        <textarea
          required
          minLength={10}
          maxLength={2000}
          value={notes}
          onChange={(event) => setNotes(event.target.value)}
        />
      </label>
      <Button disabled={save.isPending}>Save case</Button>
      {save.error ? <p className="error">{save.error.message}</p> : null}
    </form>
  );
}

function PaymentRecord({
  organisationId,
  payment,
  onChanged
}: {
  organisationId: string;
  payment: Payment;
  onChanged: () => void;
}) {
  const [amount, setAmount] = useState(
    Math.max(0, payment.captured_amount_minor - payment.refunded_amount_minor)
  );
  const refund = useMutation({
    mutationFn: () => requestRefund(organisationId, payment.id, amount, 'Owner-requested refund'),
    onSuccess: onChanged
  });
  return (
    <article className="record payment-record">
      <div>
        <strong>
          {payment.customer_name ?? 'Customer'} · {payment.service_names ?? 'Booking'}
        </strong>
        <p>
          Total {money(payment.booking_total_minor, payment.currency)} · paid{' '}
          {money(payment.captured_amount_minor, payment.currency)} · outstanding{' '}
          {money(payment.outstanding_amount_minor, payment.currency)}
        </p>
        <p>
          {payment.status} · {payment.payment_method_family} · fee{' '}
          {money(payment.processor_fee_minor, payment.currency)} · net{' '}
          {money(payment.processor_net_minor, payment.currency)} · reconciliation{' '}
          {payment.fee_reconciliation_status}
        </p>
      </div>
      <div className="refund-controls">
        <label>
          Refund minor units
          <input
            type="number"
            min="1"
            max={payment.captured_amount_minor - payment.refunded_amount_minor}
            value={amount}
            onChange={(event) => setAmount(Number(event.target.value))}
          />
        </label>
        <Button
          className="button-secondary"
          disabled={refund.isPending || amount <= 0}
          onClick={() => refund.mutate()}
        >
          Refund
        </Button>
        {refund.error ? <small className="error">{refund.error.message}</small> : null}
      </div>
    </article>
  );
}
